// Shared formatters for currency and dates.
// Use these everywhere — do NOT redefine these per-page.
// Relative-time formatting was removed deliberately on 2026-05-06: every
// user-facing timestamp shows an exact date instead.

/** YYYY-MM-DD for today in local timezone. Use as `min` on date inputs to block past-date selection. */
export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Returns true if `iso` (YYYY-MM-DD) is strictly before today.
 * Lexicographic compare works for YYYY-MM-DD.
 * Use to guard submit actions — `min` on <input type="date"> only filters
 * the native picker; users can still type past dates manually.
 */
export function isPastDate(iso: string): boolean {
  return !!iso && iso < todayIso();
}

/**
 * Earliest date selectable for a scheduled-task date input (YYYY-MM-DD).
 * - Create, or editing a today-or-future task → `today` (block past dates).
 * - Editing a task already in the past → its own `existingDate` (keep it; only
 *   block moving it *further* back). Lexicographic compare works for YYYY-MM-DD.
 * Use the returned value as both the `min` attribute and the submit-guard floor
 * (`taskDate < floor` → invalid). Do NOT also use `isPastDate` here — it compares
 * against today always and would wrongly reject an already-past task on edit.
 */
export function taskDateFloor(
  isEdit: boolean,
  existingDate: string | undefined,
  today: string,
): string {
  return isEdit && existingDate && existingDate < today ? existingDate : today;
}

/**
 * Today's date as a card header: "Weekday MM/DD/YYYY" (e.g. "Sunday 06/07/2026").
 * Long weekday from Intl; manual zero-padded MM/DD/YYYY (toLocaleDateString
 * inserts a comma after the weekday — we don't want one). Local timezone.
 */
export function formatTodayHeader(d: Date = new Date()): string {
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${weekday} ${mm}/${dd}/${yyyy}`;
}

/** Format integer cents as USD currency (e.g. 12345 → "$123.45"). */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/** Format integer cents as USD currency without cents (e.g. 12345 → "$123"). Used for Financial Dashboard where rounding to dollars is cleaner. */
export function formatCentsRounded(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format integer cents as compact currency for chart axis labels.
 * Examples: 12000 → "$120", 120000 → "$1K", 12000000 → "$120K", 1200000000 → "$12M".
 */
export function formatCentsK(cents: number): string {
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1_000)     return `$${Math.round(dollars / 1_000)}K`;
  return `$${Math.round(dollars)}`;
}

/** Format a percentage value (e.g. 15.5 → "15.5%"). */
export function formatPercent(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

/**
 * Markup % = (sell − cost) / cost, formatted — or "—" when cost ≤ 0.
 * The cost guard is load-bearing: it kills divide-by-zero/NaN/Infinity AND avoids
 * a bogus "100%" on rows whose cost is unknown (e.g. historical jobs with a 0 labor
 * cost snapshot). Use the SAME unit for cost & sell (both per consumption unit).
 */
export function formatMarkupPct(costCents: number, sellCents: number): string {
  if (!(costCents > 0)) return '—';
  return formatPercent(((sellCents - costCents) / costCents) * 100);
}

/** Margin % = (sell − cost) / sell, formatted — or "—" when sell ≤ 0. */
export function formatMarginPct(costCents: number, sellCents: number): string {
  if (!(sellCents > 0)) return '—';
  return formatPercent(((sellCents - costCents) / sellCents) * 100);
}

/** Format hours (e.g. 32.5 → "32.5 hrs"). */
export function formatHours(value: number): string {
  return `${value.toFixed(1)} hrs`;
}

/** Format an integer count with thousands separators (e.g. 1234 → "1,234"). */
export function formatInt(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

/**
 * Format a financial-dashboard variance value with explicit sign + unit.
 * `value` is the SIGNED variance (positive = better than target) already
 * normalized via signedVariance() in @gutterinv/shared.
 */
export function formatVariance(value: number, unit: 'cents' | 'pct' | 'hours' | 'int'): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const abs = Math.abs(value);
  switch (unit) {
    case 'cents':
      return `${sign}${formatCentsRounded(abs)}`;
    case 'pct':
      return `${sign}${abs.toFixed(1)}pp`;
    case 'hours':
      return `${sign}${abs.toFixed(1)} hrs`;
    case 'int':
      return `${sign}${formatInt(abs)}`;
  }
}

/**
 * Quote totals are stored pre-tax (the column is the engine subtotal).
 * Customer-facing totals are post-tax. Mirror the worker's PDF math:
 *   taxCents = round(subtotal * rate / 100)
 *   total    = subtotal + taxCents
 * Office override inputs are typed AS post-tax — back-calc to subtotal
 * via `postTaxToSubtotalCents` so the same column keeps a consistent
 * "engine subtotal" invariant downstream.
 */
export function computeQuoteTotalCents(
  subtotalCents: number | null | undefined,
  taxRatePct: number | null | undefined,
): number {
  const subtotal = subtotalCents ?? 0;
  const rate = taxRatePct ?? 0;
  if (rate <= 0) return subtotal;
  const tax = Math.round((subtotal * rate) / 100);
  return subtotal + tax;
}

export function postTaxToSubtotalCents(
  totalCents: number,
  taxRatePct: number | null | undefined,
): number {
  const rate = taxRatePct ?? 0;
  if (rate <= 0) return totalCents;
  return Math.round((totalCents * 100) / (100 + rate));
}

/**
 * Format an ISO date string as "Apr 15, 2026".
 * Handles both YYYY-MM-DD (DATE columns) and full ISO timestamps without
 * timezone drift.
 */
export function formatDate(iso: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year!, month! - 1, day!).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

